import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLatestNews, useLocalize } from '@/hooks/useLandingContent';
import { format } from 'date-fns';

export function MobileNewsScroll() {
  const { data: news } = useLatestNews(8);
  const localize = useLocalize();
  const ref = useRef<HTMLDivElement>(null);
  if (!news?.length) return null;

  const scroll = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth * 0.6;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  return (
    <section className="px-3 py-4">
      <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="text-base font-bold">Latest News & Blogs</h2>
          <Link to="/news" className="text-xs font-semibold text-primary">View All</Link>
        </div>
        <div className="relative">
          <button
            onClick={() => scroll('left')}
            aria-label="Scroll news left"
            className="absolute left-1 top-[40%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center"
          >
            <ChevronLeft className="h-4 w-4 text-primary" />
          </button>
          <button
            onClick={() => scroll('right')}
            aria-label="Scroll news right"
            className="absolute right-1 top-[40%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center"
          >
            <ChevronRight className="h-4 w-4 text-primary" />
          </button>
          <div ref={ref} className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth px-3 pb-4">
            {news.map((n: any) => (
              <Link
                key={n.id}
                to={`/news/${n.slug || n.id}`}
                className="snap-start shrink-0 basis-[calc(33.333%-6px)]"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  {n.cover_image_url && (
                    <img src={n.cover_image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  )}
                </div>
                <p className="text-[11px] font-semibold leading-snug mt-1.5 line-clamp-2">
                  {localize(n.title)}
                </p>
                {n.published_at && (
                  <p className="text-[9px] text-muted-foreground mt-0.5">
                    {format(new Date(n.published_at), 'MMM dd')}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
